import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizAPI } from '../services/api';
import { cookieUtils } from '../utils/cookies';

const AdminPanel = () => {
  const [students, setStudents] = useState([]);
  const [averagePercentage, setAveragePercentage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [csvGenerating, setCsvGenerating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin is authenticated
    if (!cookieUtils.getAdminAuth()) {
      navigate('/login');
      return;
    }

    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [studentsResponse, averageResponse] = await Promise.all([
        quizAPI.getAllStudents(),
        quizAPI.getAveragePercentage()
      ]);
      
      setStudents(studentsResponse.data);
      setAveragePercentage(averageResponse.data.averagePercentage);
    } catch (error) {
      setError('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) {
      try {
        await quizAPI.deleteStudent(studentId);
        setStudents(students.filter(student => student.id !== studentId));
        // Recalculate average
        const updatedStudents = students.filter(student => student.id !== studentId);
        const completedStudents = updatedStudents.filter(s => s.status === 'completed');
        if (completedStudents.length > 0) {
          const newAverage = completedStudents.reduce((sum, s) => sum + s.score, 0) / completedStudents.length / 30 * 100;
          setAveragePercentage(newAverage);
        } else {
          setAveragePercentage(0);
        }
      } catch (error) {
        setError('Failed to delete student');
      }
    }
  };

  const handleDownloadCSV = async () => {
    setCsvGenerating(true);
    try {
      const response = await quizAPI.downloadCSV();
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'students_data.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to generate CSV');
    } finally {
      setCsvGenerating(false);
    }
  };

  const handleLogout = () => {
    cookieUtils.clearAdminAuth();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center' }}>
            <h2>Loading admin panel...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h1 style={{ color: '#000000' }}>Admin Panel</h1>
          <button 
            className="btn btn-secondary"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="admin-panel">
          <h2 style={{ marginBottom: '20px', color: '#000000' }}>Quiz Statistics</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ color: '#000000', marginBottom: '10px' }}>Total Students</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{students.length}</p>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ color: '#000000', marginBottom: '10px' }}>Completed</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {students.filter(s => s.status === 'completed').length}
              </p>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ color: '#000000', marginBottom: '10px' }}>In Progress</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {students.filter(s => s.status === 'in_progress').length}
              </p>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ color: '#000000', marginBottom: '10px' }}>Average Score</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {averagePercentage.toFixed(1)}%
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#000000' }}>Student Management</h3>
            <button 
              className="btn btn-success"
              onClick={handleDownloadCSV}
              disabled={csvGenerating}
            >
              {csvGenerating ? 'Generating...' : 'Download CSV'}
            </button>
          </div>
        </div>

        <div className="student-list">
          {students.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: '#666'
            }}>
              <p>No students have taken the quiz yet.</p>
            </div>
          ) : (
            students.map(student => (
              <div key={student.id} className="student-item">
                <div className="student-info">
                  <div className="student-name">{student.name}</div>
                  <div className="student-score">
                    Score: {student.score}/30 ({Math.round(student.score / 30 * 100)}%)
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                    Started: {new Date(student.startTime).toLocaleString()}
                    {student.endTime && (
                      <span> | Completed: {new Date(student.endTime).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className={`student-status status-${student.status}`}>
                    {student.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                  <button 
                    className="btn btn-danger"
                    onClick={() => handleDeleteStudent(student.id)}
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
